import {
  MENTOR_REPLY_TIME,
  type Subscription
} from '@algoacademy/backend/modules/marketplace/enums'
import type { AdvancedMentoringProductDTO } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/input.dto/input.dto'
import { ORDER_TYPE } from '@algoacademy/backend/modules/payment/endpoints/mutations/stripe-checkout/types'
import type { GetAllBookingsForMonthOutputDTO } from '@algoacademy/backend/modules/phone-call-booking/endpoints/queries/get-all-bookings-for-month/output.dto'
import { type Dispatch, type SetStateAction, useEffect, useState } from 'react'
import {
  calculateAdvancedMentoringPrice,
  DEFAULT_HOURS_WITH_MENTOR,
  DEFAULT_LINES_OF_CODE
} from '../../../prices'

type MentoringDetails = GetAllBookingsForMonthOutputDTO['bookings'][number]['mentoringDetails']

function toNumber(value: boolean | null | undefined): number {
  return value === true ? 1 : 0
}

export function getDefaultAdvancedProduct(subscription: Subscription): AdvancedMentoringProductDTO {
  return {
    order: ORDER_TYPE.ADVANCED_MENTORING,
    price: calculateAdvancedMentoringPrice({
      hoursWithMentor: DEFAULT_HOURS_WITH_MENTOR,
      mentorReplyTime: MENTOR_REPLY_TIME.STANDARD,
      linesOfCode: DEFAULT_LINES_OF_CODE,
      applicationHelp: 0,
      careerCoaching: 0
    }),
    subscription,
    email: '',
    hoursWithMentor: DEFAULT_HOURS_WITH_MENTOR,
    mentorReplyTime: MENTOR_REPLY_TIME.STANDARD,
    linesOfCode: DEFAULT_LINES_OF_CODE,
    applicationHelp: 0,
    careerCoaching: 0,
    name: 'Mentoring spersonalizowany',
    quantity: 1
  }
}

type UseInitMentoringProductWithMentoringDetailsDataProps = {
  mentoringDetails?: MentoringDetails
  activeSubscriptionTab: Subscription
}

export function useInitMentoringProductWithMentoringDetailsData({
  mentoringDetails,
  activeSubscriptionTab
}: UseInitMentoringProductWithMentoringDetailsDataProps): [
  AdvancedMentoringProductDTO,
  Dispatch<SetStateAction<AdvancedMentoringProductDTO>>
] {
  const [advancedMentoringProduct, setAdvancedMentoringProduct] =
    useState<AdvancedMentoringProductDTO>(() => getDefaultAdvancedProduct(activeSubscriptionTab))

  useEffect(() => {
    if (!mentoringDetails?.advancedMentoringDetails || !mentoringDetails) {
      return
    }

    const details = mentoringDetails.advancedMentoringDetails
    const hoursWithMentor = details.hoursWithMentor ?? DEFAULT_HOURS_WITH_MENTOR
    const mentorReplyTime = details.mentorReplyTime ?? MENTOR_REPLY_TIME.STANDARD
    const linesOfCode = details.linesOfCode ?? DEFAULT_LINES_OF_CODE
    const applicationHelp = toNumber(details.applicationHelp)
    const careerCoaching = toNumber(details.careerCoaching)

    setAdvancedMentoringProduct(prev => ({
      ...prev,
      subscription: activeSubscriptionTab,
      price: calculateAdvancedMentoringPrice({
        hoursWithMentor,
        mentorReplyTime,
        linesOfCode,
        applicationHelp,
        careerCoaching
      }),
      hoursWithMentor,
      mentorReplyTime,
      linesOfCode,
      applicationHelp,
      careerCoaching
    }))
  }, [mentoringDetails, activeSubscriptionTab])

  return [advancedMentoringProduct, setAdvancedMentoringProduct]
}
